/**
 * Deep links: /projects/studyninja?scene=<id>&lens=<persona>
 *
 * Pure functions, no window access, so they are unit-testable and safe in
 * server components. The shell reads the initial URL on mount and mirrors
 * state back with history.replaceState (never a navigation).
 */

import { REFERENCE_SCENES, type ReferenceSceneId } from '../reference/types';
import { DEFAULT_PERSONA, isPersona, type Persona } from './types';

export const SCENE_PARAM = 'scene';
export const LENS_PARAM = 'lens';

/** Accepts the canonical id and the 1-based scene number ("3"). */
export function parseSceneParam(raw: string | null | undefined): ReferenceSceneId | null {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  if ((REFERENCE_SCENES as readonly string[]).includes(value)) return value as ReferenceSceneId;
  const index = Number.parseInt(value, 10);
  if (Number.isInteger(index) && index >= 1 && index <= REFERENCE_SCENES.length) return REFERENCE_SCENES[index - 1];
  return null;
}

export function parseLensParam(raw: string | null | undefined): Persona | null {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  return isPersona(value) ? value : null;
}

export interface DeepLinkState {
  scene: ReferenceSceneId | null;
  persona: Persona | null;
}

export function parseDeepLink(search: string): DeepLinkState {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return {
    scene: parseSceneParam(params.get(SCENE_PARAM)),
    persona: parseLensParam(params.get(LENS_PARAM)),
  };
}

/**
 * Serialize the current state into a query string. The default persona and
 * the first scene are omitted so the canonical URL stays clean; other params
 * already present in `search` are preserved untouched.
 */
export function buildDeepLink(search: string, scene: ReferenceSceneId, persona: Persona): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (scene === REFERENCE_SCENES[0]) params.delete(SCENE_PARAM);
  else params.set(SCENE_PARAM, scene);
  if (persona === DEFAULT_PERSONA) params.delete(LENS_PARAM);
  else params.set(LENS_PARAM, persona);
  const query = params.toString();
  return query ? `?${query}` : '';
}
